"use client";

import { c as _c } from "react/compiler-runtime";
import { useLocaleContext, useTranslation } from "@/app/i18n/client";
import LoadingSpinnerBlock from "@/components/LoadingSpinner/LoadingSpinnerBlock";
import { FormControl, FormItem, FormLabel, FormNestedMessages } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { AddressWithLocation } from "@/types/location";
import { useDebounce } from "@/utils/hooks/use-debounce";
import { useGoogleMaps } from "@/utils/google-maps-context";
import { autocompleteAddress, AutocompletePrediction } from "@/utils/placesApiCalls";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ControllerRenderProps, FieldPath } from "react-hook-form";
import { FaRegCircleCheck } from "react-icons/fa6";
import { BusrequestFormSchema } from "../Home/HomeV2Content/busrequest-helper";
import { ParseKeys } from "i18next";
type BusReqAddrFieldValues = Pick<BusrequestFormSchema, "from" | "to" | "address">;
type BusReqAddrFieldPath<TBRAFV extends BusReqAddrFieldValues> = Extract<FieldPath<TBRAFV>, "from" | "to" | "address">;
type Props<TFieldValues extends BusReqAddrFieldValues, TFieldName extends BusReqAddrFieldPath<TFieldValues>> = {
  label?: string;
  labelClassName?: string;
  type?: "region" | "address";
} & ControllerRenderProps<TFieldValues, TFieldName>;
const translationKeys = new Map([["number", "ERRORKEYS.ADDRESS.NUMBER"] as const, ["street", "ERRORKEYS.ADDRESS.STREET"] as const, ["city", "ERRORKEYS.ADDRESS.CITY"] as const, ["country", "ERRORKEYS.ADDRESS.COUNTRY"] as const, ["countryISO3166Code", "ERRORKEYS.ADDRESS.COUNTRYCODE"] as const, ["postal_code", "ERRORKEYS.ADDRESS.ZIP"] as const]) as unknown as Map<string, ParseKeys<"common">>;
export function AddressInputInner(t0) {
  const $ = _c(53);
  let field;
  let label;
  let labelClassName;
  let name;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      label,
      name,
      labelClassName,
      type: t1,
      value,
      ...field
    } = t0);
    $[0] = t0;
    $[1] = field;
    $[2] = label;
    $[3] = labelClassName;
    $[4] = name;
    $[5] = t1;
    $[6] = value;
  } else {
    field = $[1];
    label = $[2];
    labelClassName = $[3];
    name = $[4];
    t1 = $[5];
    value = $[6];
  }
  const type = t1 === undefined ? "address" : t1;
  const {
    t
  } = useTranslation();
  const [addressInput, setAddressInput] = useState(value?.address ?? "");
  let t2;
  if ($[7] !== addressInput || $[8] !== name || $[9] !== type) {
    t2 = {
      address: addressInput,
      id: name,
      geocodeType: type
    };
    $[7] = addressInput;
    $[8] = name;
    $[9] = type;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const {
    data,
    isLoading,
    isEnabled
  } = useAddressQuery(t2);
  const [showInput, setShowInput] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState();
  let t3;
  let t4;
  if ($[11] !== value) {
    t3 = () => {
      setAddressInput(value?.address ?? "");
      setSelectedAddress(value);
      !value && setShowInput(true);
    };
    t4 = [value];
    $[11] = value;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  const inputRef = useRef(null);
  let t5;
  let t6;
  if ($[14] !== field) {
    t5 = () => {
      field.ref(inputRef.current);
    };
    t6 = [field, inputRef];
    $[14] = field;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  if ($[17] !== field || $[18] !== name) {
    t7 = function makeAddressDisplayBlock(data_0) {
      return data_0.map(addr => <AddressDisplay key={`address-option-${name}-${addr.placeId}`} address={{
        ...addr,
        type: "prediction"
      }} isSelected={false} onSelect={addr_0 => {
        field.onChange(addr_0);
        setShowInput(false);
      }} />);
    };
    $[17] = field;
    $[18] = name;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const makeAddressDisplayBlock = t7;
  const t8 = labelClassName ?? "leading-5";
  let t9;
  if ($[20] !== label || $[21] !== t) {
    t9 = label ?? t("MAIN.HOMEV2.BUSREQUEST.ADDR");
    $[20] = label;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <FormLabel className={t8}>{t9}</FormLabel>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = field.disabled;
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => setAddressInput(e.target.value);
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = !showInput && "hidden";
  let t14;
  if ($[27] !== t13) {
    t14 = cn("h-auto rounded-none bg-white p-4 text-neutral-950 focus:border-matchbus md:text-lg", t13);
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== addressInput || $[30] !== field.disabled || $[31] !== t14) {
    t15 = <Input disabled={t11} ref={inputRef} name="search" autoComplete="off" type="text" value={addressInput} onChange={t12} className={t14} />;
    $[29] = addressInput;
    $[30] = field.disabled;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== selectedAddress || $[34] !== showInput) {
    t16 = selectedAddress && <AddressDisplay isSelected={true} onSelect={() => {
      setShowInput(true);
    }} address={{
      ...selectedAddress,
      type: "address"
    }} className={cn(showInput && "hidden")} />;
    $[33] = selectedAddress;
    $[34] = showInput;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== addressInput || $[37] !== data || $[38] !== isEnabled || $[39] !== isLoading || $[40] !== makeAddressDisplayBlock || $[41] !== showInput) {
    t17 = isLoading && isEnabled ? <LoadingSpinnerBlock small={true} className="w-auto basis-60" /> : data && data.length > 0 && showInput && !!addressInput ? makeAddressDisplayBlock(data) : null;
    $[36] = addressInput;
    $[37] = data;
    $[38] = isEnabled;
    $[39] = isLoading;
    $[40] = makeAddressDisplayBlock;
    $[41] = showInput;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17) {
    t18 = <div className="relative"><div className="absolute top-0 z-10 flex w-full flex-col p-0">{t17}</div></div>;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t15 || $[46] !== t16 || $[47] !== t18) {
    t19 = <FormControl><div>{t15}{t16}{t18}</div></FormControl>;
    $[45] = t15;
    $[46] = t16;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FormNestedMessages translationKeys={translationKeys} />;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t10 || $[51] !== t19) {
    t21 = <FormItem className="relative flex min-h-32 flex-col">{t10}{t19}{t20}</FormItem>;
    $[50] = t10;
    $[51] = t19;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  return t21;
}
type useAddressQueryProps = {
  address?: string;
  id: string;
  geocodeType?: "address" | "region";
};
function useAddressQuery(t0) {
  const $ = _c(21);
  const {
    address,
    id,
    geocodeType: t1
  } = t0;
  const geocodeType = t1 === undefined ? "address" : t1;
  const queryTerm = useDebounce(address, 300);
  const lng = useLocaleContext();
  const {
    isLoaded,
    loadNow
  } = useGoogleMaps();
  let t2;
  if ($[0] !== isLoaded || $[1] !== loadNow) {
    t2 = () => {
      if (!isLoaded) {
        loadNow();
      }
    };
    $[0] = isLoaded;
    $[1] = loadNow;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== geocodeType || $[5] !== id || $[6] !== lng || $[7] !== queryTerm) {
    t4 = [id, queryTerm, geocodeType, lng];
    $[4] = geocodeType;
    $[5] = id;
    $[6] = lng;
    $[7] = queryTerm;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== geocodeType || $[10] !== lng || $[11] !== queryTerm) {
    t5 = async () => {
      const res = await autocompleteAddress(queryTerm, lng, geocodeType === "region" ? undefined : "locality");
      return res;
    };
    $[9] = geocodeType;
    $[10] = lng;
    $[11] = queryTerm;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = !!queryTerm && isLoaded;
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    };
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const {
    data,
    isLoading,
    isEnabled
  } = useQuery(t7);
  let t8;
  if ($[17] !== data || $[18] !== isEnabled || $[19] !== isLoading) {
    t8 = {
      data,
      isLoading,
      isEnabled
    };
    $[17] = data;
    $[18] = isEnabled;
    $[19] = isLoading;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
const AddressDisplay = t0 => {
  const $ = _c(26);
  const {
    address,
    isSelected,
    onSelect,
    className
  } = t0;
  let t1;
  if ($[0] !== address.address || $[1] !== address.formattedAddress || $[2] !== address.type) {
    t1 = address.type === "prediction" ? address.formattedAddress.split(", ") : address.address.split(", ");
    $[0] = address.address;
    $[1] = address.formattedAddress;
    $[2] = address.type;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const formattedAddressParts = t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("w-full border-x-0 border-b-0 border-solid border-secondary last:border-b-2", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = isSelected && "border-2 border-solid";
  let t4;
  if ($[6] !== t3) {
    t4 = cn("relative box-border w-full border-matchbus bg-offWhite p-2 font-normal leading-tight text-secondary hover:bg-babyBlue", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== address || $[9] !== onSelect) {
    t5 = async e => {
      e.preventDefault();
      onSelect(address.type === "prediction" ? await address.fillPlace() : address);
    };
    $[8] = address;
    $[9] = onSelect;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== formattedAddressParts) {
    t6 = formattedAddressParts.map(_temp);
    $[11] = formattedAddressParts;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = isSelected && "block";
  let t8;
  if ($[13] !== t7) {
    t8 = cn("absolute bottom-3 right-3 hidden h-5 w-5 text-matchbus", t7);
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <FaRegCircleCheck className={t8} />;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t9) {
    t10 = <button className={t4} type="button" onClick={t5}>{t6}{t9}</button>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== isSelected || $[23] !== t10 || $[24] !== t2) {
    t11 = <div data-selected={isSelected} className={t2}>{t10}</div>;
    $[22] = isSelected;
    $[23] = t10;
    $[24] = t2;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};
function _temp(addrPart, i, arr) {
  return <Fragment key={i}><span className="leading-tight text-inherit opacity-70">{addrPart}</span>{i < arr.length && <br />}</Fragment>;
}